// Copyright © Perilous Games, Ltd., 2025. All Rights Reserved. Coded by Alexander Fields https://www.alexanderfields.me on 2025-07-02 17:26:25

/**
 * @fileoverview Main application component with route configuration
 * @module App
 * @description
 *   Root React component that configures client-side routing using React Router.
 *   Uses lazy loading with React.lazy() and Suspense for code splitting.
 *   Routes include public pages (home, gallery, downloads, login, signup),
 *   agreement pages (EULA, privacy policy), and authenticated dashboard routes
 *   with nested child routes for user account management, billing, and game features.
 *   Wrapped in HelmetProvider for dynamic meta tag management (SEO).
 */

import React, { Suspense } from 'react'
import './App.css'
import './index.css'
import {
  BrowserRouter as Router,
  Routes,
  Route
} from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import Loader from './components/shared/Loader'

const AdAstra = React.lazy(() => import('./components/dashboard/adAstra/AdAstra'))
const Analytics = React.lazy(() => import('./components/dashboard/analytics/Analytics'))
const APIs = React.lazy(() => import('./components/dashboard/apis/APIs'))
const Billing = React.lazy(() => import('./components/dashboard/billing/Billing'))
const ChangePassword = React.lazy(() => import('./components/dashboard/changePassword/ChangePassword'))
const Logging = React.lazy(() => import('./components/dashboard/logging/Logging'))
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'))
const EndUser = React.lazy(() => import('./components/dashboard/agreements/EndUser'))
const ForgotPassword = React.lazy(() => import('./pages/login/ForgotPassword'))
const Friends = React.lazy(() => import('./components/dashboard/friends/Friends'))
const Gallery = React.lazy(() => import('./components/dashboard/gallery/Gallery'))
const Games = React.lazy(() => import('./components/dashboard/games/Games'))
const HomePage = React.lazy(() => import('./pages/home/HomePage'))
const LoginPage = React.lazy(() => import('./pages/login/LoginPage'))
const NotFound = React.lazy(() => import('./pages/notFound/NotFound'))
const PrivacyPolicy = React.lazy(() => import('./components/dashboard/agreements/PrivacyPolicy'))
const SignUpPage = React.lazy(() => import('./pages/signup/SignUpPage'))
const ChooseDisplayNamePage = React.lazy(() => import('./pages/chooseDisplayName/ChooseDisplayNamePage'))
const UpdateProfile = React.lazy(() => import('./components/dashboard/updateProfile/UpdateProfile'))
const Downloads = React.lazy(() => import('./pages/downloads/Downloads'))
const AboutPage = React.lazy(() => import('./pages/about/AboutPage'))
const AdAstraImperium = React.lazy(() => import('./pages/adAstraImperium/AdAstraImperium'))
const GameDataEditor = React.lazy(() => import('./components/dashboard/admin/gameData/GameDataEditor'))
const AdminPlayerReports = React.lazy(() => import('./components/dashboard/admin/playerReports/PlayerReports'))
const AdminSecurity = React.lazy(() => import('./components/dashboard/admin/security/Security'))
const AdminFileUpload = React.lazy(() => import('./components/dashboard/admin/fileUpload/AdminFileUpload'))
const BugReport = React.lazy(() => import('./components/dashboard/bugReport/BugReport'))
const PlayerReport = React.lazy(() => import('./components/dashboard/playerReport/PlayerReport'))
const PatchNotesManager = React.lazy(() => import('./components/dashboard/admin/patchNotes/PatchNotesManager'))

/**
 * @component App
 * @description Root application component with route configuration.
 *   Configures all client-side routes with lazy-loaded components for
 *   optimized bundle splitting. Displays a Loader component while chunks load.
 * @returns {JSX.Element} Application with router and all configured routes
 */
function App () {
  return (
    <HelmetProvider>
      <Suspense fallback={<Loader />}>
        <Router>
          <Routes>
          <Route path='/' element={<HomePage />} />
          <Route path='about' element={<AboutPage />} />
          <Route path='login' element={<LoginPage />} />
          <Route path='forgot' element={<ForgotPassword />} />
          <Route path='signup' element={<SignUpPage />} />
          <Route path='choose-display-name' element={<ChooseDisplayNamePage />} />
          <Route path='gallery' element={<Gallery />} />
          <Route path='downloads' element={<Downloads />} />
          <Route path='ad-astra-imperium' element={<AdAstraImperium />} />
          <Route path='agreements/end-user' element={<EndUser />} />
          <Route path='agreements/privacy-policy' element={<PrivacyPolicy />} />
          <Route path='/dashboard/*' element={<Dashboard />}>
            <Route path='ad-astra' element={<AdAstra />} />
            <Route path='analytics' element={<Analytics />} />
            <Route path='apis' element={<APIs />} />
            <Route path='billing' element={<Billing />} />
            <Route path='changePassword' element={<ChangePassword />} />
            <Route path='friends' element={<Friends />} />
            <Route path='gallery' element={<Gallery />} />
            <Route path='games' element={<Games />} />
            <Route path='logging' element={<Logging />} />
            <Route path='game-data' element={<GameDataEditor />} />
            <Route path='admin/player-reports' element={<AdminPlayerReports />} />
            <Route path='admin/security' element={<AdminSecurity />} />
            <Route path='admin/file-upload' element={<AdminFileUpload />} />
            <Route path='admin/patch-notes' element={<PatchNotesManager />} />
            <Route path='bug-report' element={<BugReport />} />
            <Route path='player-report' element={<PlayerReport />} />
            <Route path='updateProfile' element={<UpdateProfile />} />
            <Route path='agreements/end-user' element={<EndUser />} />
            <Route path='agreements/privacy-policy' element={<PrivacyPolicy />} />
          </Route>
          {/* Catch-all route for 404 - must be last */}
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Router>
    </Suspense>
    </HelmetProvider>
  )
}

export default App
